// The calendars screen's editors (spec 03 §4.8): the shift-pattern editor, the
// assignment and end dialogs, and the exception dialog. Each checks what it can
// before the trip (calendar-lib, mirroring §3.3a) and puts every refusal the
// server sends on the field it belongs to: a 422 `SHIFT_PATTERN_INVALID` on its
// segment rows, a 409 `OVERLAPPING_ASSIGNMENT` inline with the range it meets,
// a 422 `CALENDAR_EXCEPTION_INVALID` on its fields.

import { useState } from "react";
import type { TFunction } from "i18next";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { CheckboxField, Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import { Banner } from "../../../../components/toast";
import { formatBusinessDate } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import {
  EXCEPTION_KINDS,
  createAssignment,
  createCalendarException,
  createShiftPattern,
  patchAssignment,
  patchShiftPattern,
  useCalendarRefresh,
  type CalendarExceptionKind,
  type ShiftPattern,
  type WorkCenter,
  type WorkCenterCalendar,
} from "../hooks/calendar-queries";
import { problemCode, problemExtra } from "../hooks/planning-queries";
import {
  addSegmentsForDays,
  checkException,
  checkSegments,
  crossesMidnight,
  defaultFactor,
  exceptionInstants,
  parseFactor,
  segmentToDraft,
  segmentsToWire,
  weekdayName,
  workCenterLabel,
  type ExceptionDraft,
  type ExceptionReason,
  type SegmentDraft,
  type SegmentIssue,
  type SegmentReason,
} from "../lib/calendar-lib";
import { shortId } from "../../../../lib/short-id";

/** The one line the screen shows after a write. */
export interface CalendarNotice {
  readonly kind: "ok" | "bad" | "warn";
  readonly text: string;
}

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
const PATTERN_CODE = /^[A-Z0-9][A-Z0-9_-]{0,63}$/u;

// ---- the pattern editor ------------------------------------------------------------

function sameSegments(a: readonly SegmentDraft[], b: readonly SegmentDraft[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((x, i) => {
    const y = b[i];
    if (y === undefined) return false;
    return (
      x.weekday.trim() === y.weekday.trim() &&
      x.start.trim() === y.start.trim() &&
      x.end.trim() === y.end.trim() &&
      x.shift_code.trim() === y.shift_code.trim() &&
      x.efficiency_pct.trim() === y.efficiency_pct.trim()
    );
  });
}

/** The server's issues as the editor's, or undefined when the body carries none. */
function serverIssues(error: unknown): SegmentIssue[] | undefined {
  const raw = problemExtra(error, "issues");
  if (!Array.isArray(raw)) return undefined;
  return raw
    .filter(
      (x): x is { index: number | null; reason: SegmentReason; other_index?: number } =>
        typeof x === "object" &&
        x !== null &&
        typeof (x as { reason?: unknown }).reason === "string",
    )
    .map((x) => ({ index: x.index, reason: x.reason, other_index: x.other_index }));
}

export function PatternEditor({
  pattern,
  canEdit,
  onClose,
  onDone,
}: {
  pattern: ShiftPattern | undefined;
  canEdit: boolean;
  onClose: () => void;
  onDone: (notice: CalendarNotice) => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const refresh = useCalendarRefresh();
  const [base, setBase] = useState<ShiftPattern | undefined>(pattern);
  const [code, setCode] = useState(pattern?.code ?? "");
  const [name, setName] = useState(pattern?.name ?? "");
  const [segments, setSegments] = useState<SegmentDraft[]>(
    (pattern?.segments ?? []).map(segmentToDraft),
  );
  const [quick, setQuick] = useState({
    start: "08:00",
    end: "20:00",
    shift_code: "DAY",
    efficiency_pct: "100",
  });
  const [quickDays, setQuickDays] = useState<ReadonlySet<number>>(new Set([1, 2, 3, 4, 5]));
  const [issues, setIssues] = useState<SegmentIssue[]>([]);
  const [codeError, setCodeError] = useState<string | undefined>(undefined);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [changedNotice, setChangedNotice] = useState(false);
  const [busy, setBusy] = useState(false);

  const isNew = base === undefined;
  const editable = canEdit && (isNew || base.status === "active");
  const baseSegments = (base?.segments ?? []).map(segmentToDraft);
  const nameChanged = !isNew && name.trim() !== base.name;
  const segmentsChanged = !isNew && !sameSegments(segments, baseSegments);
  const dirty = isNew || nameChanged || segmentsChanged;

  const reload = (row: ShiftPattern) => {
    setBase(row);
    setName(row.name);
    setSegments(row.segments.map(segmentToDraft));
  };

  const setSegment = (index: number, patch: Partial<SegmentDraft>) => {
    setSegments((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    setIssues([]);
  };

  /** Reads a refusal into the editor's lines; true when it was placed. */
  const place = (error: unknown): boolean => {
    const codeOf = problemCode(error);
    if (codeOf === "SHIFT_PATTERN_INVALID") {
      setIssues(serverIssues(error) ?? []);
      return true;
    }
    if (codeOf === "SHIFT_PATTERN_CODE_TAKEN") {
      setCodeError("planning.calendars.editor.errors.codeTaken");
      return true;
    }
    if (codeOf === "SHIFT_PATTERN_CHANGED") {
      const current = problemExtra(error, "current") as ShiftPattern | undefined;
      if (current !== undefined) reload(current);
      setChangedNotice(true);
      void refresh.patterns();
      return true;
    }
    if (codeOf === "SHIFT_PATTERN_OUT_OF_SCOPE") {
      const facilities = (problemExtra(error, "facilities") ?? []) as {
        facility_name: string;
      }[];
      setProblem(
        t("planning.calendars.editor.errors.outOfScope", {
          facilities: facilities.map((f) => f.facility_name).join(", "),
        }),
      );
      return true;
    }
    if (codeOf === "SHIFT_PATTERN_IN_USE") {
      const assignments = (problemExtra(error, "assignment_ids") ?? []) as string[];
      const facilityIds = (problemExtra(error, "facility_ids") ?? []) as string[];
      setProblem(
        t("planning.calendars.editor.errors.inUse", {
          assignments: assignments.length,
          facilities: facilityIds.length,
        }),
      );
      return true;
    }
    if (codeOf === "SHIFT_PATTERN_NOT_FOUND") {
      setProblem(t("planning.calendars.editor.errors.gone"));
      return true;
    }
    if (error instanceof RequestError && error.httpStatus === 400 && error.fieldErrors.length > 0) {
      let placed = false;
      const next: SegmentIssue[] = [];
      for (const fe of error.fieldErrors) {
        const path = fe.path;
        if (path === "code" || path.endsWith(".code")) {
          setCodeError("planning.calendars.editor.errors.code");
          placed = true;
        } else if (path === "name" || path.endsWith(".name")) {
          setNameError("planning.calendars.editor.errors.name");
          placed = true;
        } else {
          const m = /segments\.(\d+)\.shift_code/u.exec(path);
          if (m !== null) {
            next.push({ index: Number(m[1]), reason: "shift_code_too_long" });
            placed = true;
          } else if (path.endsWith("segments")) {
            next.push({ index: null, reason: "too_many_segments" });
            placed = true;
          }
        }
      }
      setIssues(next);
      return placed;
    }
    return false;
  };

  const run = async (write: () => Promise<ShiftPattern>, text: (row: ShiftPattern) => string) => {
    setBusy(true);
    setProblem(undefined);
    setChangedNotice(false);
    try {
      const row = await write();
      // A pattern reaches every facility's assignments and previews.
      void refresh.all();
      onDone({ kind: "ok", text: text(row) });
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      if (!place(error))
        setProblem(t("planning.common.saveFailed", { requestId: requestIdOf(error) ?? "-" }));
    } finally {
      setBusy(false);
    }
  };

  const onSave = async () => {
    setCodeError(undefined);
    setNameError(undefined);
    const found = checkSegments(segments);
    setIssues(found);
    let bad = found.length > 0;
    if (isNew && !PATTERN_CODE.test(code.trim())) {
      setCodeError("planning.calendars.editor.errors.code");
      bad = true;
    }
    const trimmed = name.trim();
    if (trimmed.length < 1 || trimmed.length > 120) {
      setNameError("planning.calendars.editor.errors.name");
      bad = true;
    }
    const wire = segmentsToWire(segments);
    if (bad || wire === undefined) return;
    if (isNew) {
      await run(
        () => createShiftPattern({ code: code.trim(), name: trimmed, segments: wire }),
        (row) => t("planning.calendars.saved.patternCreated", { code: row.code }),
      );
      return;
    }
    if (!dirty) {
      onClose();
      return;
    }
    await run(
      () =>
        patchShiftPattern(base.id, {
          ...(nameChanged ? { name: trimmed } : {}),
          ...(segmentsChanged ? { segments: wire } : {}),
          expected_updated_at: base.updated_at,
        }),
      (row) => t("planning.calendars.saved.patternUpdated", { code: row.code }),
    );
  };

  const onStatus = async (status: "active" | "archived") => {
    if (base === undefined) return;
    await run(
      () => patchShiftPattern(base.id, { status, expected_updated_at: base.updated_at }),
      (row) =>
        t(
          status === "archived"
            ? "planning.calendars.saved.patternArchived"
            : "planning.calendars.saved.patternRestored",
          { code: row.code },
        ),
    );
  };

  const issuesOf = (index: number | null) => issues.filter((i) => i.index === index);
  const issueText = (issue: SegmentIssue) =>
    t(`planning.calendars.editor.errors.segments.${issue.reason}`, {
      other: issue.other_index !== undefined ? issue.other_index + 1 : "",
    });

  return (
    <Modal
      label={
        isNew
          ? t("planning.calendars.editor.newTitle")
          : t("planning.calendars.editor.editTitle", { code: base.code })
      }
      heading={
        isNew
          ? t("planning.calendars.editor.newTitle")
          : t("planning.calendars.editor.editTitle", { code: base.code })
      }
      closeLabel={t("planning.common.close")}
      onClose={onClose}
      pending={busy}
      className="planning-drawer planning-pattern-editor"
      testId="planning-pattern-editor"
    >
      <div className="planning-drawer-section">
        {!canEdit ? (
          <p className="planning-note">{t("planning.calendars.editor.readOnly")}</p>
        ) : null}
        {base?.status === "archived" ? (
          <Banner kind="neutral" data-testid="planning-pattern-archived">
            {t("planning.calendars.editor.archivedNote")}
          </Banner>
        ) : null}
        {changedNotice ? (
          <Banner kind="warn" data-testid="planning-pattern-changed">
            {t("planning.calendars.editor.errors.changed")}
          </Banner>
        ) : null}
        {problem !== undefined ? (
          <Banner kind="bad" data-testid="planning-pattern-problem">
            {problem}
          </Banner>
        ) : null}
        <div className="planning-form">
          <div className="planning-field">
            <Field label={t("planning.calendars.editor.code")}>
              <input
                type="text"
                value={code}
                disabled={!isNew || !canEdit || busy}
                aria-invalid={codeError !== undefined || undefined}
                autoCapitalize="characters"
                data-testid="planning-pattern-code"
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  setCodeError(undefined);
                }}
              />
            </Field>
            {isNew ? (
              <span className="planning-hint">{t("planning.calendars.editor.codeHint")}</span>
            ) : null}
            {codeError !== undefined ? (
              <span className="field-error" role="alert" data-testid="planning-pattern-code-error">
                {t(codeError)}
              </span>
            ) : null}
          </div>
          <div className="planning-field">
            <Field label={t("planning.calendars.editor.name")}>
              <input
                type="text"
                value={name}
                maxLength={120}
                disabled={!editable || busy}
                aria-invalid={nameError !== undefined || undefined}
                data-testid="planning-pattern-name"
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError(undefined);
                }}
              />
            </Field>
            {nameError !== undefined ? (
              <span className="field-error" role="alert" data-testid="planning-pattern-name-error">
                {t(nameError)}
              </span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="planning-drawer-section">
        <h2>{t("planning.calendars.editor.segmentsTitle")}</h2>
        <p className="planning-hint">{t("planning.calendars.editor.segmentsHint")}</p>
        {issuesOf(null).map((issue) => (
          <p
            key={issue.reason}
            className="field-error"
            role="alert"
            data-testid="planning-pattern-segments-error"
            data-reason={issue.reason}
          >
            {issueText(issue)}
          </p>
        ))}
        {segments.length === 0 ? (
          <p className="planning-note" data-testid="planning-pattern-no-segments">
            {t("planning.calendars.editor.noSegments")}
          </p>
        ) : (
          <ol className="planning-segments">
            {segments.map((seg, index) => {
              const rowIssues = issuesOf(index);
              return (
                <li
                  // Rows have no identity of their own; their index is the one the server names.
                  key={index}
                  className="planning-segment"
                  data-testid="planning-pattern-segment"
                  data-index={index}
                  data-invalid={rowIssues.length > 0 ? "true" : "false"}
                >
                  <span className="planning-segment-n" aria-hidden="true">
                    {index + 1}
                  </span>
                  <Field label={t("planning.calendars.editor.weekday")}>
                    <Select
                      value={seg.weekday}
                      disabled={!editable || busy}
                      data-testid="planning-segment-weekday"
                      onChange={(e) => {
                        setSegment(index, { weekday: e.target.value });
                      }}
                    >
                      {WEEKDAYS.map((d) => (
                        <option key={d} value={String(d)}>
                          {weekdayName(d, i18n.language, "long")}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label={t("planning.calendars.editor.start")}>
                    <input
                      type="time"
                      step={60}
                      value={seg.start}
                      disabled={!editable || busy}
                      data-testid="planning-segment-start"
                      onChange={(e) => {
                        setSegment(index, { start: e.target.value });
                      }}
                    />
                  </Field>
                  <Field label={t("planning.calendars.editor.end")}>
                    <input
                      type="time"
                      step={60}
                      value={seg.end}
                      disabled={!editable || busy}
                      data-testid="planning-segment-end"
                      onChange={(e) => {
                        setSegment(index, { end: e.target.value });
                      }}
                    />
                  </Field>
                  <Field label={t("planning.calendars.editor.shiftCode")}>
                    <input
                      type="text"
                      value={seg.shift_code}
                      maxLength={32}
                      disabled={!editable || busy}
                      data-testid="planning-segment-shift-code"
                      onChange={(e) => {
                        setSegment(index, { shift_code: e.target.value.toUpperCase() });
                      }}
                    />
                  </Field>
                  <Field label={t("planning.calendars.editor.efficiency")}>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={seg.efficiency_pct}
                      disabled={!editable || busy}
                      data-testid="planning-segment-efficiency"
                      onChange={(e) => {
                        setSegment(index, { efficiency_pct: e.target.value });
                      }}
                    />
                  </Field>
                  {editable ? (
                    <Button
                      size="sm"
                      disabled={busy}
                      aria-label={t("planning.calendars.editor.removeSegment", { n: index + 1 })}
                      data-testid="planning-segment-remove"
                      onClick={() => {
                        setSegments((rows) => rows.filter((_, i) => i !== index));
                        setIssues([]);
                      }}
                    >
                      {t("planning.calendars.editor.remove")}
                    </Button>
                  ) : null}
                  {crossesMidnight(seg) ? (
                    <span
                      className="planning-hint planning-segment-note"
                      data-testid="planning-segment-crosses"
                    >
                      {t("planning.calendars.editor.crosses")}
                    </span>
                  ) : null}
                  {rowIssues.map((issue) => (
                    <span
                      key={`${issue.reason}-${String(issue.other_index ?? "")}`}
                      className="field-error planning-segment-note"
                      role="alert"
                      data-testid="planning-segment-error"
                      data-reason={issue.reason}
                    >
                      {issueText(issue)}
                    </span>
                  ))}
                </li>
              );
            })}
          </ol>
        )}
      </div>
      {editable ? (
        <div className="planning-drawer-section" data-testid="planning-pattern-quick">
          <h2>{t("planning.calendars.editor.quickTitle")}</h2>
          <p className="planning-hint">{t("planning.calendars.editor.quickHint")}</p>
          <fieldset className="planning-days">
            <legend className="ds-field-label">{t("planning.calendars.editor.days")}</legend>
            {WEEKDAYS.map((d) => (
              <CheckboxField
                key={d}
                label={weekdayName(d, i18n.language)}
                checked={quickDays.has(d)}
                disabled={busy}
                data-testid={`planning-quick-day-${String(d)}`}
                onChange={(on) => {
                  const next = new Set(quickDays);
                  if (on) next.add(d);
                  else next.delete(d);
                  setQuickDays(next);
                }}
              />
            ))}
          </fieldset>
          <div className="planning-form">
            <Field label={t("planning.calendars.editor.start")}>
              <input
                type="time"
                step={60}
                value={quick.start}
                disabled={busy}
                data-testid="planning-quick-start"
                onChange={(e) => {
                  setQuick({ ...quick, start: e.target.value });
                }}
              />
            </Field>
            <Field label={t("planning.calendars.editor.end")}>
              <input
                type="time"
                step={60}
                value={quick.end}
                disabled={busy}
                data-testid="planning-quick-end"
                onChange={(e) => {
                  setQuick({ ...quick, end: e.target.value });
                }}
              />
            </Field>
            <Field label={t("planning.calendars.editor.shiftCode")}>
              <input
                type="text"
                value={quick.shift_code}
                maxLength={32}
                disabled={busy}
                data-testid="planning-quick-shift-code"
                onChange={(e) => {
                  setQuick({ ...quick, shift_code: e.target.value.toUpperCase() });
                }}
              />
            </Field>
            <Field label={t("planning.calendars.editor.efficiency")}>
              <input
                type="text"
                inputMode="numeric"
                value={quick.efficiency_pct}
                disabled={busy}
                data-testid="planning-quick-efficiency"
                onChange={(e) => {
                  setQuick({ ...quick, efficiency_pct: e.target.value });
                }}
              />
            </Field>
          </div>
          <div>
            <Button
              size="sm"
              disabled={busy || quickDays.size === 0}
              data-testid="planning-quick-add"
              onClick={() => {
                setSegments((rows) => addSegmentsForDays(rows, quick, [...quickDays]));
                setIssues([]);
              }}
            >
              {t("planning.calendars.editor.addSegments")}
            </Button>
          </div>
        </div>
      ) : null}
      <div className="planning-drawer-section planning-dialog-actions">
        {!isNew && canEdit ? (
          base.status === "active" ? (
            <Button
              variant="danger"
              disabled={busy || (dirty && !isNew)}
              title={dirty ? t("planning.calendars.editor.dirtyArchive") : undefined}
              data-testid="planning-pattern-archive"
              onClick={() => void onStatus("archived")}
            >
              {t("planning.calendars.editor.archive")}
            </Button>
          ) : (
            <Button
              disabled={busy}
              data-testid="planning-pattern-restore"
              onClick={() => void onStatus("active")}
            >
              {t("planning.calendars.editor.restore")}
            </Button>
          )
        ) : null}
        <Button disabled={busy} data-testid="planning-pattern-cancel" onClick={onClose}>
          {t("planning.common.cancel")}
        </Button>
        {editable ? (
          <Button
            variant="primary"
            busy={busy}
            busyLabel={t("planning.common.saving")}
            disabled={!dirty}
            data-testid="planning-pattern-save"
            onClick={() => void onSave()}
          >
            {t("planning.common.save")}
          </Button>
        ) : null}
      </div>
    </Modal>
  );
}

// ---- assignments -------------------------------------------------------------------

/** The overlap line of a 409 `OVERLAPPING_ASSIGNMENT`, naming the range it meets. */
function overlapText(error: unknown, t: TFunction, locale: string): string {
  const overlapping = problemExtra(error, "overlapping") as
    { valid_from: string; valid_to: string | null } | undefined;
  if (overlapping === undefined) return t("planning.calendars.assignments.errors.overlapUnknown");
  return overlapping.valid_to === null
    ? t("planning.calendars.assignments.errors.overlapOpen", {
        from: formatBusinessDate(overlapping.valid_from, locale),
      })
    : t("planning.calendars.assignments.errors.overlap", {
        from: formatBusinessDate(overlapping.valid_from, locale),
        to: formatBusinessDate(overlapping.valid_to, locale),
      });
}

type AssignField = "workCenter" | "pattern" | "validFrom" | "validTo";

export function AssignDialog({
  facilityId,
  today,
  workCenters,
  patterns,
  onClose,
  onDone,
}: {
  facilityId: string;
  today: string;
  workCenters: readonly WorkCenter[];
  patterns: readonly ShiftPattern[];
  onClose: () => void;
  onDone: (notice: CalendarNotice) => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const refresh = useCalendarRefresh();
  const [wcId, setWcId] = useState("");
  const [patternId, setPatternId] = useState("");
  const [validFrom, setValidFrom] = useState(today);
  const [validTo, setValidTo] = useState("");
  const [errors, setErrors] = useState<Partial<Record<AssignField, string>>>({});
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const sortedWcs = [...workCenters].sort((a, b) => a.code.localeCompare(b.code));

  const onSave = async () => {
    const next: Partial<Record<AssignField, string>> = {};
    if (wcId === "" || patternId === "" || validFrom === "")
      next.validFrom = t("planning.calendars.assignments.errors.required");
    else if (validTo !== "" && validTo < validFrom)
      next.validTo = t("planning.calendars.assignments.errors.reversed");
    setErrors(next);
    setFailure(undefined);
    if (Object.keys(next).length > 0) return;
    setBusy(true);
    try {
      const row = await createAssignment({
        facility_id: facilityId,
        work_center_id: wcId,
        shift_pattern_id: patternId,
        valid_from: validFrom,
        valid_to: validTo === "" ? null : validTo,
      });
      void refresh.assignments(facilityId);
      void refresh.preview(facilityId);
      const wc = workCenters.find((w) => w.id === wcId);
      onDone({
        kind: "ok",
        text: t("planning.calendars.saved.assigned", {
          pattern: row.shift_pattern_code,
          workCenter: wc === undefined ? shortId(wcId) : workCenterLabel(wc, i18n.language),
        }),
      });
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      const code = problemCode(error);
      if (code === "OVERLAPPING_ASSIGNMENT")
        setErrors({ validFrom: overlapText(error, t, i18n.language) });
      else if (code === "WORK_CENTER_NOT_FOUND")
        setErrors({ workCenter: t("planning.calendars.assignments.errors.workCenterGone") });
      else if (code === "SHIFT_PATTERN_NOT_FOUND")
        setErrors({ pattern: t("planning.calendars.assignments.errors.patternGone") });
      else if (error instanceof RequestError && error.httpStatus === 400)
        setErrors({ validTo: t("planning.calendars.assignments.errors.reversed") });
      else setFailure(t("planning.common.saveFailed", { requestId: requestIdOf(error) ?? "-" }));
    } finally {
      setBusy(false);
    }
  };

  const fieldError = (name: AssignField) =>
    errors[name] !== undefined ? (
      <span
        className="field-error"
        role="alert"
        data-testid="planning-assign-error"
        data-field={name}
      >
        {errors[name]}
      </span>
    ) : null;

  return (
    <Modal
      label={t("planning.calendars.assignments.dialog.title")}
      heading={t("planning.calendars.assignments.dialog.title")}
      closeLabel={t("planning.common.close")}
      onClose={onClose}
      pending={busy}
      className="planning-dialog"
      testId="planning-assign-dialog"
    >
      {failure !== undefined ? <Banner kind="bad">{failure}</Banner> : null}
      <div className="planning-form planning-dialog-form">
        <div className="planning-field">
          <Field label={t("planning.calendars.assignments.dialog.workCenter")}>
            <Select
              value={wcId}
              disabled={busy}
              data-testid="planning-assign-work-center"
              onChange={(e) => {
                setWcId(e.target.value);
                setErrors({});
              }}
            >
              <option value="">{t("planning.calendars.assignments.dialog.choose")}</option>
              {sortedWcs.map((wc) => (
                <option key={wc.id} value={wc.id}>
                  {workCenterLabel(wc, i18n.language)}
                </option>
              ))}
            </Select>
          </Field>
          {fieldError("workCenter")}
        </div>
        <div className="planning-field">
          <Field label={t("planning.calendars.assignments.dialog.pattern")}>
            <Select
              value={patternId}
              disabled={busy}
              data-testid="planning-assign-pattern"
              onChange={(e) => {
                setPatternId(e.target.value);
                setErrors({});
              }}
            >
              <option value="">{t("planning.calendars.assignments.dialog.choose")}</option>
              {patterns.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
          {fieldError("pattern")}
        </div>
        <div className="planning-field">
          <Field label={t("planning.calendars.assignments.dialog.validFrom")}>
            <input
              type="date"
              value={validFrom}
              disabled={busy}
              data-testid="planning-assign-from"
              onChange={(e) => {
                setValidFrom(e.target.value);
                setErrors({});
              }}
            />
          </Field>
          {fieldError("validFrom")}
        </div>
        <div className="planning-field">
          <Field label={t("planning.calendars.assignments.dialog.validTo")}>
            <input
              type="date"
              value={validTo}
              disabled={busy}
              data-testid="planning-assign-to"
              onChange={(e) => {
                setValidTo(e.target.value);
                setErrors({});
              }}
            />
          </Field>
          <span className="planning-hint">
            {t("planning.calendars.assignments.dialog.validToHint")}
          </span>
          {fieldError("validTo")}
        </div>
      </div>
      <div className="planning-dialog-actions">
        <Button disabled={busy} onClick={onClose}>
          {t("planning.common.cancel")}
        </Button>
        <Button
          variant="primary"
          busy={busy}
          busyLabel={t("planning.common.saving")}
          data-testid="planning-assign-save"
          onClick={() => void onSave()}
        >
          {t("planning.calendars.assignments.dialog.save")}
        </Button>
      </div>
    </Modal>
  );
}

export function EndAssignmentDialog({
  facilityId,
  row,
  today,
  workCenterName,
  onClose,
  onDone,
}: {
  facilityId: string;
  row: WorkCenterCalendar;
  today: string;
  workCenterName: string;
  onClose: () => void;
  onDone: (notice: CalendarNotice) => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const refresh = useCalendarRefresh();
  // The dialog opens on a last day: ending is why it was opened. Ticking "No end
  // date" reopens an assignment that has one.
  const [openEnded, setOpenEnded] = useState(false);
  const [validTo, setValidTo] = useState(
    row.valid_to ?? (today < row.valid_from ? row.valid_from : today),
  );
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const onSave = async () => {
    const next = openEnded ? null : validTo;
    if (next !== null && (next === "" || next < row.valid_from)) {
      setError(t("planning.calendars.assignments.errors.reversed"));
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await patchAssignment(row.id, next);
      void refresh.assignments(facilityId);
      void refresh.preview(facilityId);
      onDone({
        kind: "ok",
        text:
          next === null
            ? t("planning.calendars.saved.reopened", { workCenter: workCenterName })
            : t("planning.calendars.saved.ended", {
                workCenter: workCenterName,
                date: formatBusinessDate(next, i18n.language),
              }),
      });
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      const code = problemCode(err);
      if (code === "OVERLAPPING_ASSIGNMENT") setError(overlapText(err, t, i18n.language));
      else if (code === "SHIFT_PATTERN_NOT_FOUND")
        setError(t("planning.calendars.assignments.errors.patternArchived"));
      else if (code === "WORK_CENTER_CALENDAR_NOT_FOUND") {
        void refresh.assignments(facilityId);
        setError(t("planning.calendars.assignments.errors.gone"));
      } else if (err instanceof RequestError && err.httpStatus === 400)
        setError(t("planning.calendars.assignments.errors.reversed"));
      else setError(t("planning.common.saveFailed", { requestId: requestIdOf(err) ?? "-" }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      label={t("planning.calendars.assignments.endDialog.title", { workCenter: workCenterName })}
      heading={t("planning.calendars.assignments.endDialog.title", { workCenter: workCenterName })}
      closeLabel={t("planning.common.close")}
      onClose={onClose}
      pending={busy}
      className="planning-dialog"
      testId="planning-end-dialog"
    >
      <p className="planning-hint">
        {row.shift_pattern_name} · {formatBusinessDate(row.valid_from, i18n.language)}
      </p>
      <div className="planning-form planning-dialog-form">
        <div className="planning-field">
          <Field label={t("planning.calendars.assignments.endDialog.validTo")}>
            <input
              type="date"
              value={openEnded ? "" : validTo}
              min={row.valid_from}
              disabled={busy || openEnded}
              data-testid="planning-end-to"
              onChange={(e) => {
                setValidTo(e.target.value);
                setError(undefined);
              }}
            />
          </Field>
          <CheckboxField
            label={t("planning.calendars.assignments.endDialog.openEnded")}
            checked={openEnded}
            disabled={busy}
            data-testid="planning-end-open"
            onChange={(on) => {
              setOpenEnded(on);
              setError(undefined);
            }}
          />
          {error !== undefined ? (
            <span className="field-error" role="alert" data-testid="planning-end-error">
              {error}
            </span>
          ) : null}
        </div>
      </div>
      <div className="planning-dialog-actions">
        <Button disabled={busy} onClick={onClose}>
          {t("planning.common.cancel")}
        </Button>
        <Button
          variant="primary"
          busy={busy}
          busyLabel={t("planning.common.saving")}
          data-testid="planning-end-save"
          onClick={() => void onSave()}
        >
          {t("planning.calendars.assignments.endDialog.save")}
        </Button>
      </div>
    </Modal>
  );
}

// ---- exceptions --------------------------------------------------------------------

type ExceptionField = "start" | "end" | "factor" | "note" | "scope";

/** Which field each refusal lands on. */
const EXCEPTION_FIELD: Readonly<
  Record<
    ExceptionReason | "kind_unknown" | "starts_at_malformed" | "ends_at_malformed",
    ExceptionField
  >
> = {
  starts_malformed: "start",
  starts_at_malformed: "start",
  ends_malformed: "end",
  ends_at_malformed: "end",
  ends_not_after_starts: "end",
  factor_malformed: "factor",
  factor_out_of_range: "factor",
  factor_above_one_for_kind: "factor",
  note_too_long: "note",
  kind_unknown: "factor",
};

export function ExceptionDialog({
  facilityId,
  timezone,
  today,
  workCenters,
  onClose,
  onDone,
}: {
  facilityId: string;
  timezone: string;
  today: string;
  workCenters: readonly WorkCenter[];
  onClose: () => void;
  onDone: (notice: CalendarNotice) => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const refresh = useCalendarRefresh();
  const [scope, setScope] = useState("");
  const [draft, setDraft] = useState<ExceptionDraft>({
    kind: "holiday",
    allDay: true,
    startDate: today,
    startTime: "08:00",
    endDate: today,
    endTime: "20:00",
    factor: defaultFactor("holiday"),
    note: "",
  });
  const [factorTouched, setFactorTouched] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<ExceptionField, string[]>>>({});
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const sortedWcs = [...workCenters].sort((a, b) => a.code.localeCompare(b.code));

  const set = (patch: Partial<ExceptionDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setErrors({});
  };

  const show = (reasons: readonly string[]) => {
    const next: Partial<Record<ExceptionField, string[]>> = {};
    for (const reason of reasons) {
      const field =
        (EXCEPTION_FIELD as Partial<Record<string, ExceptionField>>)[reason] ?? "factor";
      next[field] = [...(next[field] ?? []), t(`planning.calendars.exceptions.errors.${reason}`)];
    }
    setErrors(next);
  };

  const onSave = async () => {
    const reasons = checkException(draft, timezone);
    setFailure(undefined);
    if (reasons.length > 0) {
      show(reasons);
      return;
    }
    const { startsAt, endsAt } = exceptionInstants(draft, timezone);
    if (startsAt === undefined || endsAt === undefined) return;
    setBusy(true);
    try {
      const note = draft.note.trim();
      await createCalendarException({
        facility_id: facilityId,
        work_center_id: scope === "" ? null : scope,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
        kind: draft.kind,
        capacity_factor: parseFactor(draft.factor) ?? draft.factor,
        ...(note.length > 0 ? { note } : {}),
      });
      void refresh.exceptions(facilityId);
      void refresh.preview(facilityId);
      onDone({
        kind: "ok",
        text: t("planning.calendars.saved.exceptionAdded", {
          kind: t(`planning.calendars.exceptions.kinds.${draft.kind}`),
        }),
      });
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      const code = problemCode(error);
      if (code === "CALENDAR_EXCEPTION_INVALID") {
        const raw = problemExtra(error, "reasons");
        show(Array.isArray(raw) ? raw.filter((r): r is string => typeof r === "string") : []);
      } else if (code === "WORK_CENTER_NOT_FOUND") {
        setErrors({ scope: [t("planning.calendars.exceptions.errors.workCenterGone")] });
      } else {
        setFailure(t("planning.common.saveFailed", { requestId: requestIdOf(error) ?? "-" }));
      }
    } finally {
      setBusy(false);
    }
  };

  const errorsOf = (field: ExceptionField) =>
    (errors[field] ?? []).map((text) => (
      <span
        key={text}
        className="field-error"
        role="alert"
        data-testid="planning-exception-error"
        data-field={field}
      >
        {text}
      </span>
    ));

  return (
    <Modal
      label={t("planning.calendars.exceptions.dialog.title")}
      heading={t("planning.calendars.exceptions.dialog.title")}
      closeLabel={t("planning.common.close")}
      onClose={onClose}
      pending={busy}
      className="planning-dialog"
      testId="planning-exception-dialog"
    >
      {failure !== undefined ? <Banner kind="bad">{failure}</Banner> : null}
      <div className="planning-form planning-dialog-form">
        <div className="planning-field">
          <Field label={t("planning.calendars.exceptions.dialog.scope")}>
            <Select
              value={scope}
              disabled={busy}
              data-testid="planning-exception-scope"
              onChange={(e) => {
                setScope(e.target.value);
                setErrors({});
              }}
            >
              <option value="">{t("planning.calendars.exceptions.wholeFacility")}</option>
              {sortedWcs.map((wc) => (
                <option key={wc.id} value={wc.id}>
                  {workCenterLabel(wc, i18n.language)}
                </option>
              ))}
            </Select>
          </Field>
          {errorsOf("scope")}
        </div>
        <div className="planning-field">
          <Field label={t("planning.calendars.exceptions.dialog.kind")}>
            <Select
              value={draft.kind}
              disabled={busy}
              data-testid="planning-exception-kind"
              onChange={(e) => {
                const kind = e.target.value as CalendarExceptionKind;
                set(factorTouched ? { kind } : { kind, factor: defaultFactor(kind) });
              }}
            >
              {EXCEPTION_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`planning.calendars.exceptions.kinds.${k}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </div>
      <CheckboxField
        label={t("planning.calendars.exceptions.dialog.allDay")}
        checked={draft.allDay}
        disabled={busy}
        data-testid="planning-exception-all-day"
        onChange={(on) => {
          set({ allDay: on });
        }}
      />
      <div className="planning-form planning-dialog-form">
        <div className="planning-field">
          <Field label={t("planning.calendars.exceptions.dialog.startDate")}>
            <input
              type="date"
              value={draft.startDate}
              disabled={busy}
              data-testid="planning-exception-start-date"
              onChange={(e) => {
                const startDate = e.target.value;
                set(draft.endDate < startDate ? { startDate, endDate: startDate } : { startDate });
              }}
            />
          </Field>
          {!draft.allDay ? (
            <Field label={t("planning.calendars.exceptions.dialog.startTime")}>
              <input
                type="time"
                step={60}
                value={draft.startTime}
                disabled={busy}
                data-testid="planning-exception-start-time"
                onChange={(e) => {
                  set({ startTime: e.target.value });
                }}
              />
            </Field>
          ) : null}
          {errorsOf("start")}
        </div>
        <div className="planning-field">
          <Field label={t("planning.calendars.exceptions.dialog.endDate")}>
            <input
              type="date"
              value={draft.endDate}
              disabled={busy}
              data-testid="planning-exception-end-date"
              onChange={(e) => {
                set({ endDate: e.target.value });
              }}
            />
          </Field>
          {!draft.allDay ? (
            <Field label={t("planning.calendars.exceptions.dialog.endTime")}>
              <input
                type="time"
                step={60}
                value={draft.endTime}
                disabled={busy}
                data-testid="planning-exception-end-time"
                onChange={(e) => {
                  set({ endTime: e.target.value });
                }}
              />
            </Field>
          ) : null}
          {errorsOf("end")}
        </div>
        <div className="planning-field">
          <Field label={t("planning.calendars.exceptions.dialog.factor")}>
            <input
              type="text"
              inputMode="decimal"
              value={draft.factor}
              disabled={busy}
              data-testid="planning-exception-factor"
              onChange={(e) => {
                setFactorTouched(true);
                set({ factor: e.target.value });
              }}
            />
          </Field>
          <span className="planning-hint">
            {t("planning.calendars.exceptions.dialog.factorHint")}
          </span>
          {errorsOf("factor")}
        </div>
        <div className="planning-field">
          <Field label={t("planning.calendars.exceptions.dialog.note")}>
            <textarea
              value={draft.note}
              rows={2}
              maxLength={500}
              disabled={busy}
              data-testid="planning-exception-note"
              onChange={(e) => {
                set({ note: e.target.value });
              }}
            />
          </Field>
          {errorsOf("note")}
        </div>
      </div>
      <div className="planning-dialog-actions">
        <Button disabled={busy} onClick={onClose}>
          {t("planning.common.cancel")}
        </Button>
        <Button
          variant="primary"
          busy={busy}
          busyLabel={t("planning.common.saving")}
          data-testid="planning-exception-save"
          onClick={() => void onSave()}
        >
          {t("planning.calendars.exceptions.dialog.save")}
        </Button>
      </div>
    </Modal>
  );
}
